<template>
  <v-row wrap>
    <v-col sm="12">
      <v-card flat>
        <v-card-title>
          <v-text-field
            append-icon="search"
            :label="t('search')"
            single-line
            hide-details
            v-model="search"
            variant="underlined"
          />
        </v-card-title>
      </v-card>
      <v-card flat border>
        <v-card-text class="d-block px-0 py-0">
          <v-data-table-server
            v-if="vehiclesList"
            :headers="headers"
            :items="vehiclesList"
            :items-length="vehiclesListTotalCount"
            :loading="vehiclesListLoading"
            :search="search"
            :items-per-page-text="rowsPerPageText"
            :items-per-page-options="rowsPerPageItems"
            v-model:items-per-page="pagination.rowsPerPage"
            v-model:page="pagination.page"
            @update:options="getVehicles"
            @update:sort-by="sortEvent"
          >
            <template v-slot:headers="{isSorted, getSortIcon, toggleSort }">
              <tr>
                <template v-for="header in headers" :key="header.key" >
                  <th class="custom_header_table custom-bg" :style="{width: `${header.width}px !important`}" >
                    <span class="d-flex align-center" @click="header.sortable ? toggleSort(header) : ''">
                      <span class="cursor-pointer">{{ header.title }}</span>
                      <template v-if="isSorted(header)">
                        <v-icon :icon="getSortIcon(header)" size="small"></v-icon>
                      </template>
                    </span>
                  </th>
                </template>
              </tr>
            </template>

            <template v-slot:item="{ item } : {item : any}">
              <tr>
                <td>
                  <img v-if="item.vehicle_type?.image_url" :src="item.vehicle_type.image_url" class="vehicle-img" />
                </td>
                <td>
                  <router-link class="custom-a" :to="{ name: 'VehiclesEdit', params: { id: item.id }}">
                    {{ item.name }}
                  </router-link>
                </td>
                <td>{{ item.license_plate }}</td>
                <td>{{ item.external_id }}</td>
                <td>{{ item.branch?.name }}</td>
                <td>{{ item.vehicle_type?.brand }}</td>
                <td>{{ item.vehicle_type?.model }}</td>
                <td>{{ translateVehicleTypeName(item.vehicle_type?.name) }}</td>
                <td>{{ item.year }}</td>
                <td>{{ translateFuelType(item.fuel_type) }}</td>
                <td>{{ translateStatus(item.status) }}</td>
                <td class="text-right">
                  <v-btn v-if="auth.check('edit vehicles')" variant="text" :to="{ name: 'VehiclesEdit', params: { id: item.id }}" icon flat>
                    <v-icon color="grey">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn v-if="auth.check('delete vehicles')" variant="text" v-on:click="deleteVehicle(item.id)" icon flat>
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table-server>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { datatableOptions } from '@/mixins/datatableOptions'
import Say from '@/utils/Say'
import { ref, computed, onBeforeMount } from 'vue'
import store from '@/store'
import { vehicleStore } from '@/store/vehicles'
import { useI18n } from 'vue-i18n'
import sayErrorResponse from '@/mixins/sayErrorResponse'
import { useFuelTypes } from '@/utils/fuelTypes'
import { useVehicleTypeNames } from '@/utils/vehicleTypeNames'
import { useVehicleStatuses } from '@/utils/vehicleStatuses'
import { useAuth } from '@/utils/auth'

const auth = useAuth()
const { t } = useI18n()
const { translateFuelType } = useFuelTypes()
const { translateVehicleTypeName } = useVehicleTypeNames()
const { translateStatus } = useVehicleStatuses()

const search = ref('')
const headers: any = ref([
  { title: '', key: 'image', sortable: false, width: 10 },
  { title: t('name', 1), key: 'name', sortable: true },
  { title: t('licensePlate'), key: 'license_plate', sortable: true },
  { title: t('externalId'), key: 'external_id', sortable: true },
  { title: t('branch', 1), key: 'branch', sortable: false },
  { title: t('brand'), key: 'brand', sortable: false },
  { title: t('model'), key: 'model', sortable: false },
  { title: t('vehicleType', 1), key: 'vehicle_type', sortable: false },
  { title: t('year'), key: 'year', sortable: true },
  { title: t('fuelType'), key: 'fuel_type', sortable: false },
  { title: t('status'), key: 'status', sortable: true },
  { key: '', sortable: false, width: 100 }
])
const vehiclesList = computed(() => vehicleStore.getters.vehicles)
const vehiclesListLoading = computed(() => vehicleStore.getters.vehicleListLoading)
const vehiclesListTotalCount = computed(() => vehicleStore.getters.vehicleListTotal)

const { rowsPerPageItems, rowsPerPageText, pagination } = datatableOptions()

onBeforeMount(() => {
  store.commit('app.setPageTitle', t('vehicle', 2))
  store.commit('app.setBreadcrumbs', [
    { text: t('vehicle', 2) }
  ])
})

const getVehicles = () => {
  vehicleStore.dispatch('fetchVehicles', Object.assign(
    pagination.value,
    {
      search: search.value,
    }
  ))
}

const deleteVehicle = (id: any) => {
  let confirmAction = confirm(t('vehicleDeleteConfirm'))
  if (confirmAction) {
    vehicleStore.dispatch('deleteVehicle', id).then(() => {
      Say('success', t('vehicleDeleteSuccess'))
    }).catch((err) => {
      sayErrorResponse(err)
    })
  }
}

const sortEvent = (value: any) => {
  if(value.length === 0) {
    return
  }
  pagination.value.sortBy = value[0].key
  pagination.value.descending = value[0].order === 'desc' ? true : false
}
</script>

<style scoped>
.vehicle-img {
  max-width: 48px;
  max-height: 24px;
  display: block;
  margin: -5px auto;
}
</style>
